import { MenuItemContainer } from "@cervval.js/client-html5-core/src/components/menuItemContainer";
import { DomTools } from "@cervval.js/client-html5-core/tools";
import { LogService } from "@cervval.js/common/services";
import { getVOFValue } from "@cervval.js/common/src/tools/object.tools";
import { CollectionTools, ObjectTools } from "@cervval.js/common/tools";
import { NavigationService, PageDefinitions } from "../navigation.service";
import { MenuDefinition, MenuDefinitions, MenuItemDefinition, MenuItemInsertion } from "./menuDefinition";
import { DEFAULT_MENU_FACTORY_UID, DEFAULT_MENU_SECTION, MenuFactories, MenuFactory, MenuItemHtmlElements } from "./menuFactory";

/**
 * The menu helper creates the menu items defined in the page definitions depending on user permissions
 * It also manages the 'active' display of the menu items and the visibility of dynamic menus
 */
export interface MenuHelper<BasePageName, PermissionContext = void> {

    /**
     * Set menu configuration settings
     * @param appContainer the app container (eg body)
     * @param menus menus that will integrate pages links
     * @param factories factories to create menu elements
     */
    configure(appContainer: HTMLElement, menus?: MenuDefinitions, factories?: MenuFactories): void;

    /**
     * Inject menu items of provided pages in concerned menus
     * @param dynamicPermissionMenus Will clear cache  in order to rebuilt it with new list of pages, you can't use this option when rebuilding menu in a page which you don't have access.
     */
    setupMenus(navigation: NavigationService<BasePageName, PermissionContext>, log: LogService, pages: PageDefinitions, dynamicPermissionMenus?: boolean): void;

    /**
     * Update 'active' links and show/hide dynamic menus
     */
    updateMenus(navigation: NavigationService<BasePageName, PermissionContext>, log: LogService, params: { previousPage?: BasePageName, newPage: BasePageName }): void;

}

/**
 * The MenuItemCache retains HtmlElements and definitions for a single menu item
 * It is kept in the cache structure of the helper in order to ease manipulation of items 
 */
export interface MenuItemCache {
    pageName: string | undefined;

    separatorElement?: HTMLElement;// separator that was inserted in the dom before the element
    element?: MenuItemHtmlElements;

    itemDefinition: MenuItemDefinition;// menu item definition
    insertion: MenuItemInsertion;// menu item insertion
}

/**
 * The MenuCache retains everything related to the menu manipulation
 * It is used to allow dynamic menus additions, we can insert menu elements among items that were created before
 */
export interface MenuCache {
    definition: MenuDefinition;
    factory: MenuFactory;
    menuElement: HTMLElement;
    menuElementBySectionKey: { [sectionKey: string]: HTMLElement };// sectionKey = sectionUid or "undefined" if no sectionUid provided
    menuItemsBySectionKey: { [sectionKey: string]: MenuItemCache[] };// sectionKey = sectionUid or "undefined" if no sectionUid provided
    staticItems?: { item: MenuItemCache, factory: MenuFactory, dynamicUrl: boolean }[]; // static items are items that are always visible but linked to a dynamic menu
}

/**
 * Parameter to update parent menu visibility from its uid and activate section
 */
type UpdateParentParameters = { menuUid: string, activeRef: string, active: boolean, updateDynamicMenus: boolean }

/**
 * Parameter to update menu visibility from page name
 */
type UpdateParameters<BasePageName> = { pageName: BasePageName, active: boolean, updateDynamicMenus: boolean, logErrorIfPageNotFound?: boolean }

export class DefaultMenuHelper<BasePageName, PermissionContext = void> {

    protected _settings?: {
        appContainer: HTMLElement,// the app container (eg body)
        menus: MenuDefinitions,// menus that will integrate pages links
        factories: MenuFactories// factories to create menu elements
    };

    /**
     * Record created menu items by menuUid  (filled by setupMenus)
     * This is used to allow dynamic menus additions, we can insert menu elements among items that were created before
     */
    protected _cacheByMenuUid: {
        [menuUid: string]: MenuCache
    } = {};

    /**
     * Record created menu items by menuUid
     * Quickly identify menus related to a page (filled by setupMenus)
     */
    protected _cacheByPageName: {
        [pageName: string]: {
            menuItemsByMenu: { [menuUid: string]: MenuItemCache[] }// items by menuUid (regardless section => array)
        }
    } = {};

    /**
     * Set menu configuration settings
     */
    public configure(appContainer: HTMLElement, menus?: MenuDefinitions, factories?: MenuFactories): void {
        if (this._settings) {
            throw new Error("Configuration was already done");
        }

        // initialize service settings
        this._settings = {
            appContainer,
            menus: menus ?? {},
            factories: factories ?? {}
        };
    }

    /**
     * @returns the factory uid or the default factory if no uid is provided
     */
    protected _getFactoryUid(uid?: string): string {
        return uid ?? DEFAULT_MENU_FACTORY_UID;
    }

    /**
     * Prepare the menu item insertion (insert in menu cache)
     */
    protected _prepareMenuInsertion(navigation: NavigationService<BasePageName, PermissionContext>, menuInsertion: MenuItemInsertion, itemDefinition: MenuItemDefinition, log: LogService, pageNameOrMenu: string | MenuCache, encounteredMenuUids: Record<string, boolean>): void {
        if (!this._settings) {
            throw new Error("configure was not called");
        }

        // inserting for a page or for a dynamic menu (static link)
        const pageName = typeof pageNameOrMenu === "string" ? pageNameOrMenu : undefined;
        const parentMenuCache: MenuCache | undefined = typeof pageNameOrMenu === "string" ? undefined : pageNameOrMenu;
        const elementDebugId = pageName ? "page " + pageName : "menu " + parentMenuCache?.definition.menuRef;

        const menuDefinition = this._settings.menus[menuInsertion.menuUid];
        // check data is all set correctly
        if (!menuDefinition) {
            log.error("Menu " + menuInsertion.menuUid + " not found for " + elementDebugId);
            return;
        }
        if (menuDefinition.sections && !menuInsertion.sectionUid) {
            menuInsertion.sectionUid = DEFAULT_MENU_SECTION;
        }
        if (menuInsertion.sectionUid && (!menuDefinition.sections || !menuDefinition.sections[menuInsertion.sectionUid])) {
            log.error("Menu " + menuInsertion.menuUid + " has no section " + menuInsertion.sectionUid + " requested by " + elementDebugId);
            return;
        }
        const factoryUid = this._getFactoryUid(menuDefinition.menuFactoryUid);
        const factory = this._settings.factories[factoryUid];
        if (!factory) {
            log.error("Menu factory " + factoryUid + " not found for menu " + menuInsertion.menuUid);
            return;
        }

        encounteredMenuUids[menuInsertion.menuUid] = true;

        // compute page url 
        if (!itemDefinition.url) {
            if (pageName) {
                itemDefinition.url = navigation.getUrl(pageName as unknown as BasePageName);
            } else if (!parentMenuCache?.definition.dynamicMenuStaticItem?.dynamicUrl) {
                log.error("No url defined on item definition for " + elementDebugId);
                return;
            }// else if url is dynamic, url will be set later
        }

        const sectionKey = String(menuInsertion.sectionUid);

        // initialize _cacheByMenuUid
        let menuCache = this._cacheByMenuUid[menuInsertion.menuUid];
        if (!menuCache) {
            // if no ref is provided it means the ref is the same as menuUid
            const menuRef = menuDefinition.menuRef ?? menuInsertion.menuUid;
            const menuElement = DomTools.findElementByRef(this._settings.appContainer, menuRef);
            if (!menuElement) {
                log.error("Menu element with ref " + menuRef + " not found for menu " + menuInsertion.menuUid);
                return;
            }

            menuCache = this._cacheByMenuUid[menuInsertion.menuUid] = {
                definition: menuDefinition,
                menuElement,
                factory,
                menuElementBySectionKey: { "undefined": menuElement },// "undefined" => default section = menu
                menuItemsBySectionKey: {}
            };

            if (menuDefinition.sections) {
                // get sections by ref
                for (const subSectionKey in menuDefinition.sections) {
                    const sectionRef = menuDefinition.sections[subSectionKey];
                    const sectionElement = DomTools.getElementByRef(menuElement, sectionRef);
                    menuCache.menuElementBySectionKey[subSectionKey] = sectionElement;
                    // we search for items with a menuOrder attribute in order to insert new items at the right position
                    this._detectExistingOrderedItems(menuCache, sectionElement, menuInsertion.menuUid, subSectionKey);
                }
            } else {
                // we search for items with a menuOrder attribute in order to insert new items at the right position
                this._detectExistingOrderedItems(menuCache, menuElement, menuInsertion.menuUid, sectionKey);
            }

            // hide dynamic menus by default
            if (menuDefinition.isDynamic) {
                factory.setDisplay(menuElement, false);
            }

            /**
             * If menuRef is a CjsDropDown and dynamicUrl is set, clicking on the button will navigate to the last navigated page of this menu 
             * or the defaultUrl if no navigation was done before
             */
            if (menuDefinition.dynamicUrl) {
                menuCache.staticItems ??= [];
                const staticItem = this._buildMenuItemCacheFromExistingElement({
                    element: menuCache.menuElement, menuUid: menuInsertion.menuUid, sectionUid: menuInsertion.menuUid
                });
                // register item so that its hRef will get updated when navigation occurs
                menuCache.staticItems.push({
                    factory,
                    item: staticItem,
                    dynamicUrl: true
                });
                if (menuDefinition.dynamicUrl.defaultUrl) {
                    // setup default url
                    this._setHref(staticItem, menuDefinition.dynamicUrl.defaultUrl, log);
                }
            }
        }

        // create menuItemsBySectionKey if needed
        menuCache.menuItemsBySectionKey[sectionKey] ??= [];

        // push item cache entry into appropriate section
        const menuItemCache: MenuItemCache = {
            element: undefined,
            insertion: menuInsertion,
            itemDefinition: itemDefinition,
            pageName
        };
        menuCache.menuItemsBySectionKey[sectionKey].push(menuItemCache);

        if (parentMenuCache) {
            // inserting for a static item link to a dynamic menu => register link
            parentMenuCache.staticItems ??= [];
            parentMenuCache.staticItems.push({
                factory,
                item: menuItemCache,
                dynamicUrl: parentMenuCache.definition.dynamicMenuStaticItem?.dynamicUrl ?? false
            });
        }

    }

    /**
     * Identifies elements already present in the menuElement that have a menuOrder attribute
     * in order to insert new menu elements before or after these existing elements depending on their order
     */
    private _detectExistingOrderedItems(menuCache: MenuCache, menuElement: HTMLElement, menuUid: string, sectionUid: string): void {
        const existingItems = menuElement.querySelectorAll("[menuOrder]");
        if (existingItems.length) {
            existingItems.forEach((element) => {
                const menuOrder = Number(element.getAttribute("menuOrder"));
                if (!isNaN(menuOrder)) {
                    // register element in menuItemsBySectionKey (it won't ba added again since element is defined)
                    // element will be used to when inserting items that must be inserted after (with an higher "order")
                    const existingItemCache = this._buildMenuItemCacheFromExistingElement({
                        element, menuUid, sectionUid, menuOrder
                    });
                    menuCache.menuItemsBySectionKey[sectionUid] ??= [];// create menuItemsBySectionKey if needed
                    menuCache.menuItemsBySectionKey[sectionUid].push(existingItemCache);
                }
            });
        }
    }

    /**
     * @returns a MenuItemCache corresponding to an element already present in the DOM
     */
    private _buildMenuItemCacheFromExistingElement(p: { element: Element, menuUid: string, sectionUid: string, menuOrder?: number }): MenuItemCache {
        const existingItemCache: MenuItemCache = {
            element: { mainElement: p.element as HTMLElement },
            insertion: { menuUid: p.menuUid, order: p.menuOrder, sectionUid: p.sectionUid },
            itemDefinition: { label: undefined },
            pageName: undefined
        };
        return existingItemCache;
    }


    /**
     * Inject menu items of provided pages in concerned menus
     */
    // eslint-disable-next-line complexity
    public setupMenus(navigation: NavigationService<BasePageName, PermissionContext>, log: LogService, pages: PageDefinitions, dynamicPermissionMenus?: boolean): void {
        if (!this._settings) {
            throw new Error("configure was not called");
        }

        if (dynamicPermissionMenus) {
            // Make sure everything is reset
            for (const menuUid in this._cacheByMenuUid) {
                const menuCache = this._cacheByMenuUid[menuUid];
                for (const sectionKey in menuCache.menuItemsBySectionKey) {
                    const menuItemsCache = menuCache.menuItemsBySectionKey[sectionKey];
                    for (const itemCacheKey in menuItemsCache) {
                        const menuItemCache = menuItemsCache[itemCacheKey];
                        if (menuItemCache.element) {
                            menuItemCache.element.mainElement.remove();
                        }
                    }
                }
            }
            this._clearCache();
        }

        // First check parameters are valid and register elements in the cache (they will be inserted later)
        const encounteredMenuUids: Record<string, boolean> = {};
        for (const pageName in pages) {
            const p = pages[pageName];
            if (p.menus) {
                for (const menuInsertion of p.menus) {
                    const itemDefinition = menuInsertion.item ?? p.menuItem; // menuInsertion.item overrides the "original" menuItem definition
                    if (!itemDefinition) {
                        log.error("Page " + pageName + " requires insertion into menu " + menuInsertion.menuUid + " but has no menuItem");
                        continue;
                    }
                    this._prepareMenuInsertion(navigation, menuInsertion, itemDefinition, log, pageName, encounteredMenuUids);
                }
            }
        }

        const encounteredUids = Object.keys(encounteredMenuUids);

        // FIXME permissions ne doit pas remove()
        /*
        for (const entry of Object.entries(this._settings.menus)) {
            const uid = entry[0];
            const menuDefinition = entry[1];
            if (!encounteredUids.includes(uid)) { // Check if we have page in all menus
                // If menu has no page, we remove it from navbar
                findElementByRef(document, uid)?.parentElement?.remove();
            }
        }*/

        // insert dynamicMenuStaticItem in the menus (before items)
        {
            for (const menuUid in encounteredMenuUids) {
                const menuCache = this._cacheByMenuUid[menuUid];
                if (!menuCache) {
                    log.error("Menu " + menuUid + " cache not initialized");
                    continue;
                }
                if (menuCache.definition.dynamicMenuStaticItem) {
                    const sItem = menuCache.definition.dynamicMenuStaticItem;
                    this._prepareMenuInsertion(navigation, sItem.insertion, sItem.insertion.item, log, menuCache, encounteredMenuUids);
                }
            }
        }

        // insert elements in the menus
        for (const menuUid in encounteredMenuUids) {
            const menuCache = this._cacheByMenuUid[menuUid];
            if (!menuCache) {
                log.error("Menu " + menuUid + " cache not initialized");
                continue;
            }

            for (const sectionKey in menuCache.menuItemsBySectionKey) {
                // get menu HtmlElement
                const menuElement = menuCache.menuElementBySectionKey[sectionKey];// sectionKey = sectionUid or "undefined" if no sectionUid provided

                // sort by reverse order (the last one first)
                const menuItems = menuCache.menuItemsBySectionKey[sectionKey];
                menuItems.sort((a, b) => {
                    let res = -ObjectTools.compare(a.insertion.order ?? 0, b.insertion.order ?? 0);// first sort by order
                    if (res === 0) {
                        res = -ObjectTools.compare(getVOFValue(a.itemDefinition.label), getVOFValue(b.itemDefinition.label));// then sort by label
                    }
                    return res;
                });
                // insert new elements
                let insertBefore: HTMLElement | undefined;
                for (const item of menuItems) {
                    if (!item.element) { // if element is already in the DOM, we don't need to create a new menu item
                        // create and insert element
                        item.element = menuCache.factory.newMenuItem(item.itemDefinition, menuUid, item.insertion.sectionUid);
                        if (!item.element.alreadyInserted) {
                            this._insertMenuItem(menuElement, item.element.mainElement, insertBefore ?? null);
                            if (item.insertion.separatorBefore) {
                                item.separatorElement = menuCache.factory.newSeparator(menuUid, item.insertion.sectionUid);
                                this._insertMenuItem(menuElement, item.separatorElement, item.element.mainElement);
                            }
                        }
                        // register item in _cacheByPageName
                        if (item.pageName) {
                            if (!this._cacheByPageName[item.pageName]) {
                                this._cacheByPageName[item.pageName] = { menuItemsByMenu: {} };
                            }
                            CollectionTools.addToArrayRecord(this._cacheByPageName[item.pageName].menuItemsByMenu, menuUid, item);
                        }
                    }
                    insertBefore = item.separatorElement ?? item.element.mainElement;// note that elements are in reverse order in menuItems
                }
            }
        }

        // update dynamicMenuStaticItem href if not defined
        {
            for (const menuUid in encounteredMenuUids) {
                const menuCache = this._cacheByMenuUid[menuUid];
                if (!menuCache) {
                    continue;
                }
                if (menuCache.definition.dynamicMenuStaticItem) {
                    const sItem = menuCache.definition.dynamicMenuStaticItem;
                    if (sItem.dynamicUrl && sItem.insertion.item && !sItem.insertion.item.url) {
                        // item url is not yet defined => search for first existing item
                        for (const items of Object.values(menuCache.menuItemsBySectionKey)) {
                            for (const item of items) {
                                if (item.itemDefinition.url) {
                                    const href = item.itemDefinition.url;
                                    sItem.insertion.item.url = href;

                                    // set href on static items
                                    if (menuCache.staticItems) {
                                        for (const staticItem of menuCache.staticItems) {
                                            if (staticItem?.item.element) {
                                                if (staticItem.dynamicUrl) {
                                                    // change the target of the link to the active page
                                                    this._setHref(staticItem.item, href, log);
                                                }
                                            }
                                        }
                                    }
                                    break;
                                }
                            }
                            if (sItem.insertion.item.url) {
                                // found => stop
                                break;
                            }
                        }
                    }
                }
            }
        }

    }

    /**
     * Equivalent with menuElement.insertBefore(itemElement, insertBefore) for standard menus
     * For dropdown menus, menuElement is replaced with the internal menu
     */
    protected _insertMenuItem(menuElement: HTMLElement, itemElement: HTMLElement, insertBefore: HTMLElement | null): void {
        const menuItemContainer = menuElement as unknown as MenuItemContainer;
        if (menuItemContainer.insertMenuItem) {
            // menuElement has a insertMenuItem function
            menuItemContainer.insertMenuItem(itemElement, insertBefore);
        } else {
            menuElement.insertBefore(itemElement, insertBefore);
        }
    }

    /**
     * Set href of item link
     */
    protected _setHref(item: MenuItemCache, url: string, log: LogService): void {
        const mainElementMenuItemContainer = item.element?.mainElement as unknown as MenuItemContainer | undefined;
        // change the target of the link to the provided url
        if (mainElementMenuItemContainer?.setUrl) {
            // menuElement has a setUrl function (like DropDownButtons)
            mainElementMenuItemContainer.setUrl(url);
        } else {
            const link = item.element?.linkElement;
            if (link) {
                link.href = url;
            } else {
                log.error("Target Link was not found for menu", item.insertion.menuUid);
            }
        }
    }


    /**
     * Update 'active' links and show/hide dynamic menus
     */
    public updateMenus(navigation: NavigationService<unknown, unknown>, log: LogService, params: { previousPage?: BasePageName, newPage: BasePageName }): void {
        if (!this._settings) {
            throw new Error("configure was not called");
        }
        if (params.previousPage === params.newPage) {
            return;
        }
        if (params.previousPage) {
            // remove 'active' and hide dynamic menus
            this._updatePageMenuItems({ pageName: params.previousPage, active: false, updateDynamicMenus: true }, log);
        }
        // add 'active' and show dynamic menus
        this._updatePageMenuItems({ pageName: params.newPage, active: true, updateDynamicMenus: true }, log);
    }

    /**
     * Set 'active' on every menu items of the page and show / hide dynamic menus linked to the page
     */
    protected _updatePageMenuItems(p: UpdateParameters<BasePageName>, log: LogService): void {

        const pageCache = this._cacheByPageName[String(p.pageName)];
        if (pageCache) {
            this._updateMenuItems(pageCache.menuItemsByMenu, p, log);
        } else if (p.logErrorIfPageNotFound) {
            log.error("page " + p.pageName + " was not found in the cache");
        }
    }

    /**
     * Set 'active' on parent menu items of the page and show / hide dynamic menus linked to the page
     */
    protected _updateParentPagePageMenuItems(p: UpdateParentParameters, log: LogService): void {

        /**
         * TODO since in fact we only have a "parent page", it would be simpler to directly set a "parentPageName"
         * BGS : The way it was implemented in SSX forces to have kind of page aliases, this is not very clean, we should be able to find a cleaner solution
         */

        /** Retrieve the page cache corresponding to the referenceUid of the parent reference */
        const matchingPageCacheList = Object.values(this._cacheByPageName)
            // Filter pages that contains the matching reference ID
            .filter(pageCache => {
                // Return true if page menu item corresponds to p.activeRef
                return Object.keys(pageCache.menuItemsByMenu)
                    .flatMap(menuUid => pageCache.menuItemsByMenu[menuUid])
                    .filter(menuItem => menuItem.insertion.menuUid === p.menuUid && menuItem.insertion.sectionUid === p.activeRef).length > 0;
            });

        // Updating parent items 
        matchingPageCacheList.forEach(matchingPageCache => this._updateMenuItems(matchingPageCache.menuItemsByMenu, p, log));

    }

    /**
     * Set 'active' on every provided menu items and show / hide dynamic menus linked to the page
     */
    private _updateMenuItems(menuItemsByMenu: { [menuUid: string]: MenuItemCache[] }, p: UpdateParameters<BasePageName> | UpdateParentParameters, log: LogService): void {
        for (const menuUid in menuItemsByMenu) {
            const menuItems = menuItemsByMenu[menuUid];
            const menuCache = this._cacheByMenuUid[menuUid];

            if (menuCache) {

                // Check if menu has a parent dynamic menu 
                if (menuCache.definition.dynamicParentReference) {
                    const parentReference = menuCache.definition.dynamicParentReference;
                    // If menu is linked to a "parent page", we update the menu items of the parent page (set active and show/hide its dynamic menus)
                    this._updateParentPagePageMenuItems({ ...p, ...parentReference }, log);
                }

                // set active on every menu items of the page
                for (const menuItem of menuItems) {
                    if (menuItem.element) {
                        menuCache.factory.setActive(menuItem.element, p.active);
                    }
                }

                // show or hide dynamic menus linked to the page
                if (p.updateDynamicMenus && menuCache.definition.isDynamic) {
                    menuCache.factory.setDisplay(menuCache.menuElement, p.active);
                }

                // When this menu is active, linked static items need to be active as well
                if (menuCache.staticItems) {
                    for (const staticItem of menuCache.staticItems) {
                        if (staticItem?.item.element) {
                            staticItem.factory.setActive(staticItem.item.element, p.active);
                            if (p.active && staticItem.dynamicUrl) {
                                // change the target of the link to the active page
                                this._setHref(staticItem.item, location.href, log);
                            }
                        }
                    }
                }
            }
        }
    }

    /**
     * Clear current cache
     */
    private _clearCache(): void {
        this._cacheByMenuUid = {};
        this._cacheByPageName = {};
    }

}